"use client";

import { Field as FieldPrimitive } from "@base-ui/react/field";
import { TABLE_CONTROL_CLASS, type InputProps } from "./input";
import { mergeProps } from "@base-ui/react/merge-props";
import type * as React from "react";
import { cn } from "../../lib/utils";
import type { Omittable } from "../../lib/props";

export type TextareaProps = React.ComponentPropsWithoutRef<"textarea"> &
  React.RefAttributes<HTMLTextAreaElement> & Omittable<{
    size?: "sm" | "default" | "lg" | number;
    /** Chrome treatment. `outline`: page fill in both modes (no dark tint),
     *  no shadow — the border is the control, brightening on hover; the
     *  themed border stays reserved for focus. For form fields sitting on a
     *  `background`-filled surface. */
    variant?: InputProps["variant"];
    unstyled?: boolean;
    /** Render only the textarea element: no shell span, no minimum height or
     * padding — beyond dropping the UA focus outline (the host draws the
     * focus chrome), the className passed in is ALL the styling. For hosts
     * that own the control's metrics themselves (an inline title edit that
     * wraps), where even `unstyled` keeps the inner textarea's own minimum
     * height and would grow the row. */
    bare?: boolean;
  }>;

export function Textarea({
  className,
  size = "default",
  variant,
  unstyled = false,
  bare = false,
  ref,
  ...props
}: TextareaProps): React.ReactElement {
  if (bare) {
    return (
      <textarea
        ref={ref}
        className={cn("select-text outline-none", className)}
        data-slot="textarea"
        {...props}
      />
    );
  }
  return (
    <span
      className={
        cn(
          !unstyled &&
            "relative inline-flex w-full select-text rounded-lg border border-input bg-background not-dark:bg-clip-padding text-base text-foreground ring-ring/24 transition-shadow before:pointer-events-none before:absolute before:inset-0 before:rounded-[calc(var(--radius-lg)-1px)] has-focus-visible:has-aria-invalid:border-destructive/64 has-focus-visible:has-aria-invalid:ring-destructive/16 has-aria-invalid:border-destructive/36 has-focus-visible:border-ring has-disabled:cursor-not-allowed has-disabled:opacity-64 has-focus-visible:ring-[3px] sm:text-sm dark:has-focus-visible:has-aria-invalid:ring-destructive/24",
          !unstyled &&
            (variant === "outline"
              ? "transition-[box-shadow,border-color] not-has-disabled:not-has-focus-visible:hover:border-muted-foreground/60"
              : variant === "ghost"
                ? "bg-transparent dark:bg-transparent"
                : variant === "table"
                  ? TABLE_CONTROL_CLASS
                  : "shadow-xs/5 has-[:disabled,:focus-visible,[aria-invalid]]:shadow-none not-has-disabled:has-not-focus-visible:not-has-aria-invalid:before:shadow-[0_1px_--theme(--color-black/4%)] dark:bg-input/32 dark:not-has-disabled:has-not-focus-visible:not-has-aria-invalid:before:shadow-[0_-1px_--theme(--color-white/6%)]"),
          className,
        ) || undefined
      }
      data-size={size}
      data-slot="textarea-control"
    >
      <FieldPrimitive.Control
        ref={ref}
        value={props.value}
        defaultValue={props.defaultValue}
        disabled={props.disabled}
        id={props.id}
        name={props.name}
        render={(defaultProps: React.ComponentProps<"textarea">) => (
          <textarea
            className={cn(
              "field-sizing-content min-h-17.5 w-full rounded-[inherit] px-[calc(--spacing(3)-1px)] py-[calc(--spacing(1.5)-1px)] outline-none max-sm:min-h-20.5",
              size === "sm" &&
                "min-h-16.5 px-[calc(--spacing(2.5)-1px)] py-[calc(--spacing(1)-1px)] max-sm:min-h-19.5",
              size === "lg" &&
                "min-h-18.5 py-[calc(--spacing(2)-1px)] max-sm:min-h-21.5",
            )}
            data-slot="textarea"
            {...mergeProps(defaultProps, props)}
          />
        )}
      />
    </span>
  );
}
